import React, { useState, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Edit } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';

// Import images
import edaplogo from "./img/edaplogo.png";
import unilislogo from "./img/unilislogo.png";
import rectorSig from "./img/rector_sig.png";
import './aquidedroit.css';

const AquiDeDroitTemplate = ({ app }) => {
    const [formData, setFormData] = useState({
        noms: '',
        dateNaissance: '',
        lieuNaissance: '',
        pays: '',
        matricule: '',
        diplome: 'doctorat (Ph.D)',
        specialite: '',
        option: '',
        mention: 'Très Honorable',
        anneeAcademique: '',
        numeroRef: '',
        dateEmission: new Date().toISOString().split('T')[0],
        includeSignature: true
    });
    const [documents, setDocuments] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const db = getDatabase(app);

    // Load documents from Firebase
    useEffect(() => {
        const documentsRef = ref(db, `aquidedroit/UNCR/`);
        onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const documentArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setDocuments(documentArray);
            }
        });
    }, [db]);

    // Generate QR Code
    useEffect(() => {
        const generateQRCode = async () => {
            if (!selectedDocument) return;

            let qrData = `A QUI DE DROIT - UNILIS\n`;
            Object.keys(selectedDocument).forEach(key => {
                if (key !== 'createdAt' && selectedDocument[key]) {
                    qrData += `${key}: ${selectedDocument[key]}\n`;
                }
            });
            qrData += `Status: Document Verified\nIssued by: UNILIS - Université de Lisala`;

            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (selectedDocument) generateQRCode();
    }, [selectedDocument]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Validate required fields
        const requiredFields = ['noms', 'dateNaissance', 'lieuNaissance', 'pays', 'matricule', 'diplome', 'specialite', 'option', 'mention', 'anneeAcademique', 'dateEmission'];
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].trim() === '') {
                alert(`${field} est obligatoire!`);
                return;
            }
        }

        if (isEditing && editingId) {
            // Update existing document
            const documentRefPath = ref(db, `aquidedroit/UNCR/${editingId}`);
            set(documentRefPath, {
                ...formData,
                createdAt: documents.find(doc => doc.id === editingId)?.createdAt || new Date().toISOString()
            })
                .then(() => {
                    console.log('A QUI DE DROIT document updated successfully');
                    message.success('Document modifié avec succès');
                    resetForm();
                })
                .catch((error) => {
                    console.error('Error updating A QUI DE DROIT document:', error);
                    message.error('Erreur lors de la modification');
                });
        } else {
            // Create new document
            const sanitizedName = formData.noms.replace(/[.#$[\]]/g, '_').replace(/\s+/g, '_');
            const documentRefPath = ref(db, `aquidedroit/UNCR/${sanitizedName}_${Date.now()}`);
            set(documentRefPath, {
                ...formData,
                createdAt: new Date().toISOString()
            })
                .then(() => {
                    console.log('A QUI DE DROIT document saved successfully');
                    message.success('Document créé avec succès');
                    resetForm();
                })
                .catch((error) => {
                    console.error('Error saving A QUI DE DROIT document:', error);
                    message.error('Erreur lors de la création');
                });
        }
    };

    const resetForm = () => {
        setFormData({
            noms: '',
            dateNaissance: '',
            lieuNaissance: '',
            pays: '',
            matricule: '',
            diplome: 'doctorat (Ph.D)',
            specialite: '',
            option: '',
            mention: 'Très Honorable',
            anneeAcademique: '',
            numeroRef: '',
            dateEmission: new Date().toISOString().split('T')[0],
            includeSignature: true
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditDocument = (document) => {
        setFormData({
            noms: document.noms || '',
            dateNaissance: document.dateNaissance || '',
            lieuNaissance: document.lieuNaissance || '',
            pays: document.pays || '',
            matricule: document.matricule || '',
            diplome: document.diplome || 'doctorat (Ph.D)',
            specialite: document.specialite || '',
            option: document.option || '',
            mention: document.mention || 'Très Honorable',
            anneeAcademique: document.anneeAcademique || '',
            numeroRef: document.numeroRef || '',
            dateEmission: document.dateEmission || new Date().toISOString().split('T')[0],
            includeSignature: document.includeSignature !== undefined ? document.includeSignature : true
        });
        setIsEditing(true);
        setEditingId(document.id);
        
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `aquidedroit/UNCR/${id}`))
            .then(() => {
                message.success('Document supprimé avec succès');
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const downloadPDF = async () => {
        if (!selectedDocument) return;

        const element = document.getElementById('aquidedroit-document');
        if (!element) {
            console.error('Document element not found');
            return;
        }

        const clone = element.cloneNode(true);
        clone.style.cssText = `
            width: 21cm !important;
            height: auto !important;
            min-height: 29.7cm !important;
            margin: 0 !important;
            padding: 2cm 0.5cm !important;
            background: white !important;
            position: relative !important;
            box-sizing: border-box !important;
            font-family: 'Times New Roman', serif !important;
            overflow: visible !important;
        `;

        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = `
            position: fixed;
            top: -9999px;
            left: -9999px;
            z-index: -1;
            width: 21cm;
            height: auto;
            min-height: 29.7cm;
        `;
        tempContainer.appendChild(clone);
        document.body.appendChild(tempContainer);

        try {
            // Wait for images to load
            const images = clone.querySelectorAll('img');
            await Promise.all(Array.from(images).map(img => {
                return new Promise((resolve) => {
                    if (img.complete) {
                        resolve();
                    } else {
                        img.onload = resolve;
                        img.onerror = resolve;
                    }
                });
            }));

            // Small delay to ensure rendering is complete
            await new Promise(resolve => setTimeout(resolve, 500));

            const canvas = await html2canvas(clone, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#FFFFFF',
                logging: false,
                imageTimeout: 15000,
                scrollX: 0,
                scrollY: 0,
                width: clone.scrollWidth,
                height: clone.scrollHeight
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'pt', 'a4'); // 'p' for portrait
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Calculate the aspect ratio to fit the image properly
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;
            // Center the image on the page
            const x = (pageWidth - scaledWidth) / 2;
            const y = (pageHeight - scaledHeight) / 2;
            pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
            pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'document'}_A_QUI_DE_DROIT.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Erreur lors de la génération du PDF');
        } finally {
            document.body.removeChild(tempContainer);
        }
    };

    return (
        <div className="aquidedroit-container">
            <div className="aquidedroit-form-section">
                <div style={{ textAlign: 'center' }}>
                    <img src={edaplogo} alt="Logo EDAP" className="aquidedroit-form-logo" />
                    <h2>Formulaire A QUI DE DROIT</h2>
                </div>

                <div className="card-form">
                    <form onSubmit={handleSubmit}>
                        <input
                            className="cinput"
                            type="text"
                            name="noms"
                            value={formData.noms}
                            onChange={handleInputChange}
                            placeholder="Nom complet"
                            required
                        />
                        <input
                            className="cinput"
                            type="date"
                            name="dateNaissance"
                            value={formData.dateNaissance}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            className="cinput"
                            type="text"
                            name="lieuNaissance"
                            value={formData.lieuNaissance}
                            onChange={handleInputChange}
                            placeholder="Lieu de naissance"
                            required
                        />
                        <input
                            className="cinput"
                            type="text"
                            name="pays"
                            value={formData.pays}
                            onChange={handleInputChange}
                            placeholder="Pays"
                            required
                        />
                        <input
                            className="cinput"
                            type="text"
                            name="matricule"
                            value={formData.matricule}
                            onChange={handleInputChange}
                            placeholder="Matricule"
                            required
                        />
                        <select
                            className="cinput"
                            name="diplome"
                            value={formData.diplome}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="doctorat (Ph.D)">Doctorat (Ph.D)</option>
                            <option value="master">Master</option>
                        </select>
                        <input
                            className="cinput"
                            type="text"
                            name="specialite"
                            value={formData.specialite}
                            onChange={handleInputChange}
                            placeholder="Spécialité (ex: Sciences Économique et de Gestion)"
                            required
                        />
                        <input
                            className="cinput"
                            type="text"
                            name="option"
                            value={formData.option}
                            onChange={handleInputChange}
                            placeholder="Option (ex: Finance)"
                            required
                        />
                        <select
                            className="cinput"
                            name="mention"
                            value={formData.mention}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="Très Honorable avec Félicitations du Jury">Très Honorable avec Félicitations du Jury</option>
                            <option value="Très Honorable">Très Honorable</option>
                            <option value="Honorable">Honorable</option>
                            <option value="Bien">Bien</option>
                            <option value="Assez Bien">Assez Bien</option>
                        </select>
                        <input
                            className="cinput"
                            type="text"
                            name="anneeAcademique"
                            value={formData.anneeAcademique}
                            onChange={handleInputChange}
                            placeholder="Année académique (Ex: 2024-2025)"
                            required
                        />
                        <input
                            className="cinput"
                            type="text"
                            name="numeroRef"
                            value={formData.numeroRef}
                            onChange={handleInputChange}
                            placeholder="No/réf (optionnel)"
                        />
                        <input
                            className="cinput"
                            type="date"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                        />
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="includeSignature"
                                checked={formData.includeSignature}
                                onChange={handleInputChange}
                            />
                            Inclure la signature du recteur
                        </label>
                        <button type="submit" className="submit-button">
                            {isEditing ? 'Mettre à jour le Document' : 'Créer le Document A QUI DE DROIT'}
                        </button>
                        {isEditing && (
                            <button 
                                type="button"
                                onClick={resetForm}
                                className="submit-button"
                                style={{ 
                                    backgroundColor: '#6c757d', 
                                    marginLeft: '10px',
                                    marginTop: '20px'
                                }}
                            >
                                Annuler
                            </button>
                        )}
                    </form>
                </div>

                {/* Document List */}
                <div className="aquidedroit-document-list">
                    {documents.map((document) => (
                        <div key={document.id} className="aquidedroit-document-item">
                            <button
                                className="aquidedroit-document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.noms} - A QUI DE DROIT
                            </button>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button 
                                    className="aquidedroit-edit-btn"
                                    onClick={() => handleEditDocument(document)}
                                    title="Modifier"
                                >
                                    <Edit size={15} />
                                </button>
                                <Popconfirm
                                    title="Supprimer le document"
                                    description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                    onConfirm={() => handleDeleteDocument(document.id)}
                                    okText="Oui"
                                    cancelText="Non"
                                    okButtonProps={{ danger: true }}
                                >
                                    <button className="aquidedroit-delete-btn" title="Supprimer">
                                        <Delete size={15} />
                                    </button>
                                </Popconfirm>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <ConfigProvider>
                <Modal
                    title={<span style={{ color: 'white' }}>Aperçu - A QUI DE DROIT</span>}
                    open={modalVisible}
                    onCancel={handleModalClose}
                    width="95vw"
                    style={{ top: 10 }}
                    centered={false}
                    maskClosable={true}
                    keyboard={true}
                    footer={[
                        <Button
                            key="download"
                            type="primary"
                            icon={<Download size={16} />}
                            onClick={downloadPDF}
                            style={{
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                fontWeight: 'bold'
                            }}
                        >
                            Télécharger PDF
                        </Button>,
                        <Button
                            key="close"
                            onClick={handleModalClose}
                            style={{ fontWeight: 'bold' }}
                        >
                            Fermer
                        </Button>
                    ]}
                >
                    {selectedDocument && (
                        <div className="aquidedroit-modal-content">
                            <AquiDeDroitDocument document={selectedDocument} qrCodeData={qrCodeData} />
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        </div>
    );
};

// Document Component
const AquiDeDroitDocument = ({ document: docData, qrCodeData }) => {
    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    return (
        <div id="aquidedroit-document" className="aquidedroit-document">
            {/* Header */}
            <div className="aquidedroit-header">
                <div className="aquidedroit-header-row">
                    <div className="aquidedroit-left-logo">
                        <img src={unilislogo} alt="Logo UNILIS" className="aquidedroit-logo" />
                        <div className="aquidedroit-left-text">
                            <h1 className="aquidedroit-school">ECOLE DOCTORALE</h1>
                            <h2 className="aquidedroit-subtitle">Cotutelle Internationale de Thèse et Co-diplomation</h2>
                        </div>
                    </div>
                    <div className="aquidedroit-header-center">
                        <h3 className="aquidedroit-university">UNIVERSITE DE LISALA</h3>
                        <p className="aquidedroit-center">Centre Interuniversitaire de Recherche Pluridisciplinaire (Cirep)</p>
                        <p className="aquidedroit-status">Status: Universite publique</p>
                        <p className="aquidedroit-web">Web: www.universitedelisala.ac.cd</p>
                        <p className="aquidedroit-email">Email: info@universitedelisala.ad.cd</p>
                    </div>
                    <div className="aquidedroit-right-logo">
                        <img src={edaplogo} alt="Logo EDAP" className="aquidedroit-logo" />
                    </div>
                </div>
            </div>

            {/* Black separator line */}
            <div className="aquidedroit-separator"></div>

            {/* Number reference */}
            {docData.numeroRef && (
                <div className="aquidedroit-number-ref">
                    <span>No/réf {docData.numeroRef}</span>
                </div>
            )}

            {/* Title */}
            <div className="aquidedroit-title-section">
                <h1 className="aquidedroit-main-title">A QUI DE DROIT</h1>
            </div>

            {/* Document Body */}
            <div className="aquidedroit-document-body">
                <div className="aquidedroit-main-text">
                    <p>
                        Je soussigné Professeur Abbé TEBAKABE ALOMO Donat; Recteur de l'Université de Lisala atteste par la présente que <strong>{docData.noms}</strong>, né(e) le <strong>{formatDateWithMonthName(docData.dateNaissance)}</strong> à <strong>{docData.lieuNaissance}</strong>, <strong>{docData.pays}</strong>, enregistré(e) sous No <strong>{docData.matricule}</strong>;<br/>
                        a été régulièrement inscrit au programme doctoral en cotutelle internationale et Codiplomation entre l'Universitė de Lisala en Rėpublique Dėmocratique du Congo(RDC) et l'UNCR university aux Etats Unis.<br/><br/>
                        L'impėtrant a subi avec succès les épreuves requises et rempli toutes les conditions exigées par le règlement pour l'obtention du Diplôme de <strong>{docData.diplome}</strong> en <strong>{docData.specialite}</strong>, Option : <strong>{docData.option}</strong>, obtenu avec la mention "<strong>{docData.mention}</strong>", année académique <strong>{docData.anneeAcademique}</strong> avec tous les droits et les privilèges qu'il confère.<br/><br/>
                        Références de l'Université de Lisala :<br/>
                        <strong>Statut de l' Université :</strong> Université Publique ou Université du Gouvernement de la RDC.<br/>
                        <strong>Adresse physique de l'Université:</strong> Avenue Aérodrome n° 20, Quartier 3C, Commune de Bolikango, Ville de Lisala, Province de la Mongala<br/>
                        <strong>Addresse postale: </strong>Université de Lisala; B.P 9289 Kinshasa I<br/>
                        <strong>Adresse electronique de l'Université :</strong> https://universitedelisala.ac.cd/<br/>
                        <strong>International Ranking information :</strong> https://www.unirank.org/cd/uni/university-of-lisala/<br/>
                        <strong>Reconnaissance Internationale par UNESCO :</strong> https://www.whed.net/institutions/IAU-028487<br/>
                        <strong>Reconnaissance Internationale de ses diplômes :</strong> Les diplômes de Doctorat l'Université de Lisala reçoivent<br/>
                        des équivalences au niveau mondial. Cas de World Education Services/ : https://www.wes.org/ . A titre d'Exemple Dr Meguem Edouidge (E-mail : esineng@yahoo.fr ) en fait un témoignage d'obtention de l'Equi-valence de World Éducation Services(WES), qui l'a permis de postuler à des emplois situés au Canada ou aux USA … sur la seule base de son PhD puisqu'il est déjà reconnu par le Service d'équivalence internationa-lement, organisme habilité .<br/><br/>
                        Cette attestation lui est delivre pour faire valoir ce que de droit.<br/><br/>
                        Fait à Lisala, le <strong>{formatDateWithMonthName(docData.dateEmission)}</strong>
                    </p>
                </div>
            </div>

            {/* Signature */}
            <div className="aquidedroit-signature">
                {docData.includeSignature && (
                    <div className="aquidedroit-signature-content">
                        <img src={rectorSig} alt="Signature du Recteur" className="aquidedroit-signature-image" />
                        <p className="aquidedroit-signature-name">Professeur Abbé Donat TEBAKABE Alomo</p>
                        <p className="aquidedroit-signature-title">Recteur</p>
                    </div>
                )}
                {qrCodeData && (
                    <div className="aquidedroit-qr-section">
                        <img
                            src={qrCodeData}
                            alt="QR Code - Document Verification"
                            className="qr-code"
                            style={{
                                width: '60px',
                                height: '60px',
                                border: '1px solid #000'
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AquiDeDroitTemplate;
